import { jsx as _jsx } from "react/jsx-runtime";
import { AuthError } from "@auth/client";
import React from "react";
import { AuthContext } from "./AuthContext";
/**
 * 認証状態を管理し、子コンポーネントにコンテキストを提供するプロバイダー
 *
 * @param props - AuthProviderのプロパティ
 * @returns AuthProviderコンポーネント
 *
 * @example
 * ```tsx
 * const authClient = new AuthClient({ baseUrl: 'http://localhost:3000' });
 *
 * <AuthProvider client={authClient}>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ client, passkeyClient, children, }) {
    const [user, setUser] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    // 初期化: 既存トークンのチェック
    React.useEffect(() => {
        const initAuth = async () => {
            try {
                if (client.isAuthenticated()) {
                    const currentUser = client.getCurrentUser();
                    setUser(currentUser);
                }
            }
            finally {
                setIsLoading(false);
            }
        };
        void initAuth();
    }, [client]);
    // ログイン処理
    const login = React.useCallback(async (credentials) => {
        const response = await client.login(credentials);
        setUser(response.user);
        return response;
    }, [client]);
    // ログアウト処理
    const logout = React.useCallback(async () => {
        await client.logout();
        setUser(null);
    }, [client]);
    // トークンリフレッシュ処理
    const refreshToken = React.useCallback(async () => {
        await client.refreshToken();
        const currentUser = client.getCurrentUser();
        setUser(currentUser);
    }, [client]);
    // passkey サインイン（パスワードログインと同格に認証状態を更新する）。
    // `passkeyClient` 未注入時は無効（呼出は fail-closed で reject）。
    const passkeyEnabled = passkeyClient != null;
    const signInWithPasskey = React.useCallback(async () => {
        if (passkeyClient == null) {
            throw new AuthError("INTERNAL_001", "passkey is not enabled (no passkeyClient provided)");
        }
        // signInWithPasskey は LoginResponse を返し、トークンは client 内部が共有ストレージへ
        // 書き込む。ここでは user を state へ反映して SPA の認証状態を即時更新する。
        const response = await passkeyClient.signInWithPasskey();
        setUser(response.user);
        return response;
    }, [passkeyClient]);
    // passkey 登録（サインイン済みユーザーが現在の origin の RP に credential を登録する）。
    // 認証状態は変更しない。`passkeyClient` 未注入時は無効（fail-closed で reject）。
    const registerPasskey = React.useCallback(async (input) => {
        if (passkeyClient == null) {
            throw new AuthError("INTERNAL_001", "passkey is not enabled (no passkeyClient provided)");
        }
        return passkeyClient.registerPasskey(input);
    }, [passkeyClient]);
    const value = {
        user,
        isLoading,
        isAuthenticated: user !== null,
        login,
        logout,
        refreshToken,
        passkeyEnabled,
        signInWithPasskey,
        registerPasskey,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
